import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

// 操作ボタンは通常時から塗りまたは枠線で領域を示す。
// テキストリンクだけは常時下線付きとし、操作ボタンと見分けられるようにする。
// 主操作と選択状態の配色はここで管理し、利用側で色を上書きしない。
const primaryButtonStyle =
  "bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover dark:bg-brand-hover dark:hover:bg-brand-hover/90";

const buttonVariantStyles = {
  default: primaryButtonStyle,
  primaryAction: primaryButtonStyle,
  selected:
    "border border-primary bg-primary/10 text-brand-strong shadow-xs hover:bg-primary/15 hover:text-brand-strong",
  destructive:
    "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
  outline:
    "border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
  secondary:
    "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
  link: "text-brand-strong underline underline-offset-4 hover:decoration-2",
} as const;

export const buttonVariantNames = Object.keys(buttonVariantStyles) as Array<
  keyof typeof buttonVariantStyles
>;

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-bold transition-all cursor-pointer disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50 [&>svg]:mx-0 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: buttonVariantStyles,
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

type ButtonBaseProps = Omit<React.ComponentProps<"button">, "children"> &
  VariantProps<typeof buttonVariants>;

export type ButtonAsButtonProps = ButtonBaseProps & {
  asChild?: false;
  children?: React.ReactNode;
  isLoading?: boolean;
};

type ButtonAsChildProps = ButtonBaseProps & {
  asChild: true;
  children: React.ReactElement;
  isLoading?: never;
};

export type ButtonProps = ButtonAsButtonProps | ButtonAsChildProps;

function Button({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isLoading || disabled}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {!asChild && isLoading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
